import React, { useEffect, useRef, useState } from "react";
import { useSharedForm } from "../context/FormContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SELLER_OF_RECORD = "Brass Tacks Research LLC";

const TCPA_CONSENT_TEXT =
    "By providing my phone number, I authorize Brass Tacks Research LLC and its " +
    "retirement-services partners to contact me at the number provided, including by " +
    "autodialer or prerecorded message, about Gold IRA and retirement-related services. " +
    "Consent is not required to make a purchase. Message and data rates may apply. " +
    "Reply STOP to unsubscribe.";

function getQueryParams() {
    if (typeof window === "undefined") return {};
    const sp = new URLSearchParams(window.location.search);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    const out = {};
    keys.forEach((k) => {
        const v = sp.get(k);
        if (v) out[k] = v;
    });
    return out;
}

function digitsOnly(s) {
    return (s || "").replace(/\D/g, "");
}

function formatUSPhone(input) {
    const d = digitsOnly(input).slice(0, 10);
    if (d.length === 0) return "";
    if (d.length < 4) return `(${d}`;
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export default function SubscribeForm({ instance, autoFocus = false, tone = "light" }) {
    const { values, update, submitted, setSubmitted } = useSharedForm();
    const [touched, setTouched] = useState({});
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const renderAtRef = useRef(null);

    if (renderAtRef.current === null) {
        renderAtRef.current = new Date().toISOString();
    }

    const phoneDigits = digitsOnly(values.phone);
    const phoneValid = phoneDigits.length === 10;
    const showTcpa = phoneValid;

    const emailValid = EMAIL_RE.test(values.email);
    const nameValid = values.first_name.trim().length > 0;
    const canSubmit = emailValid && nameValid && phoneValid && !loading;

    const fieldId = (name) => `${instance}-${name}`;

    const onPhoneChange = (e) => {
        update({ phone: formatUSPhone(e.target.value) });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setTouched({ first_name: true, email: true, phone: true });
        setErrorMsg("");
        if (!canSubmit) return;

        const honeypot = e.currentTarget.elements.company_website
            ? e.currentTarget.elements.company_website.value
            : "";

        setLoading(true);
        try {
            const utm = getQueryParams();
            const payload = {
                first_name: values.first_name.trim(),
                email: values.email.trim(),
                phone: phoneDigits,
                landing_url: typeof window !== "undefined" ? window.location.href : null,
                referrer: typeof document !== "undefined" ? document.referrer || null : null,
                form_render_at: renderAtRef.current,
                submitted_at: new Date().toISOString(),
                company_website: honeypot,
                consent_text_snapshot: TCPA_CONSENT_TEXT,
                consent_partner_name: SELLER_OF_RECORD,
                opt_in_method: "web_form_v1",
                ...utm,
            };

            const res = await fetch(`${API}/leads`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (res.status === 429) {
                setErrorMsg("Too many attempts. Please wait a minute and try again.");
                return;
            }
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                if (data && data.error === "submission_too_fast") {
                    setErrorMsg("Submission was too quick. Please review the form and try again.");
                    return;
                }
                throw new Error("Bad response");
            }
            setSubmitted(true);
        } catch (_) {
            setErrorMsg("Something went wrong. Try again, or write to hello@brasstacksbrief.com.");
        } finally {
            setLoading(false);
        }
    };

    const isDark = tone === "dark";
    const labelColor = isDark ? "rgba(244, 239, 229, 0.7)" : "var(--bt-mute)";
    const inputColor = isDark ? "var(--bt-paper)" : "var(--bt-ink)";
    const ruleColor = isDark ? "rgba(244, 239, 229, 0.32)" : "var(--bt-rule)";
    const helperColor = isDark ? "rgba(244, 239, 229, 0.6)" : "var(--bt-mute)";

    if (submitted) {
        return (
            <div
                role="status"
                aria-live="polite"
                data-testid={`subscribe-success-${instance}`}
                style={{
                    paddingTop: 28,
                    paddingBottom: 28,
                    borderTop: `1px solid ${isDark ? "rgba(244,239,229,0.4)" : "var(--bt-ink)"}`,
                    borderBottom: `1px solid ${ruleColor}`,
                    fontFamily: "Fraunces, Newsreader, serif",
                    fontVariationSettings: '"opsz" 60, "SOFT" 0',
                    fontWeight: 500,
                    fontSize: 22,
                    lineHeight: 1.35,
                    color: inputColor,
                    letterSpacing: "-0.012em",
                    animation: "bt-fade 360ms ease-out",
                }}
            >
                Check your inbox. The first brief is on its way.
            </div>
        );
    }

    const showNameError = touched.first_name && !nameValid;
    const showEmailError = touched.email && values.email.length > 0 && !emailValid;
    const showPhoneError = touched.phone && values.phone.length > 0 && !phoneValid;

    return (
        <form
            onSubmit={handleSubmit}
            noValidate
            data-testid={`subscribe-form-${instance}`}
            style={{ display: "flex", flexDirection: "column", gap: 22 }}
        >
            <h3
                id={`${instance}-form-heading`}
                style={{
                    position: "absolute",
                    left: "-9999px",
                    width: 1,
                    height: 1,
                    overflow: "hidden",
                }}
            >
                Subscribe to Brass Tacks Brief
            </h3>

            <Field id={fieldId("first-name")} label="First Name" labelColor={labelColor}>
                <input
                    id={fieldId("first-name")}
                    name="first_name"
                    type="text"
                    autoComplete="given-name"
                    autoFocus={autoFocus}
                    value={values.first_name}
                    onChange={(e) => update({ first_name: e.target.value })}
                    onBlur={() => setTouched((t) => ({ ...t, first_name: true }))}
                    className={`f-input${showNameError ? " error" : ""}`}
                    style={{ color: inputColor, borderBottomColor: ruleColor }}
                    aria-invalid={showNameError ? "true" : "false"}
                    aria-required="true"
                    data-testid={`${instance}-first-name-input`}
                />
                {showNameError && <ErrorLine>First name is required.</ErrorLine>}
            </Field>

            <Field id={fieldId("email")} label="Email" labelColor={labelColor}>
                <input
                    id={fieldId("email")}
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={values.email}
                    onChange={(e) => update({ email: e.target.value })}
                    onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                    className={`f-input${showEmailError ? " error" : ""}`}
                    style={{ color: inputColor, borderBottomColor: ruleColor }}
                    aria-invalid={showEmailError ? "true" : "false"}
                    aria-required="true"
                    data-testid={`${instance}-email-input`}
                />
                {showEmailError && <ErrorLine>Enter a valid email address.</ErrorLine>}
            </Field>

            <Field id={fieldId("phone")} label="Phone" labelColor={labelColor}>
                <input
                    id={fieldId("phone")}
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={values.phone}
                    onChange={onPhoneChange}
                    onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                    className={`f-input${showPhoneError ? " error" : ""}`}
                    placeholder="(555) 555-5555"
                    pattern="\([0-9]{3}\) [0-9]{3}-[0-9]{4}"
                    style={{ color: inputColor, borderBottomColor: ruleColor }}
                    aria-invalid={showPhoneError ? "true" : "false"}
                    aria-required="true"
                    aria-describedby={`${instance}-phone-helper`}
                    maxLength={14}
                    data-testid={`${instance}-phone-input`}
                />
                {showPhoneError && <ErrorLine>Enter a valid 10-digit US phone number.</ErrorLine>}
                <p
                    id={`${instance}-phone-helper`}
                    className="f-helper"
                    style={{ color: helperColor, marginTop: 10 }}
                >
                    For a free 15-minute call with a retirement specialist. Standard message
                    and data rates may apply.
                </p>
            </Field>

            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    left: "-9999px",
                    top: "auto",
                    width: 1,
                    height: 1,
                    overflow: "hidden",
                }}
            >
                <label htmlFor={fieldId("company-website")}>
                    Company website (leave blank)
                </label>
                <input
                    id={fieldId("company-website")}
                    name="company_website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    defaultValue=""
                />
            </div>

            {showTcpa && <TcpaConsent text={TCPA_CONSENT_TEXT} dark={isDark} />}

            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 8 }}>
                <button
                    type="submit"
                    disabled={!canSubmit}
                    data-testid={`${instance}-submit-button`}
                    aria-disabled={!canSubmit}
                    style={{
                        background: isDark ? "var(--bt-paper)" : "var(--bt-ink)",
                        color: isDark ? "var(--bt-ink)" : "var(--bt-paper)",
                        fontFamily: "Geist, system-ui, sans-serif",
                        fontWeight: 500,
                        fontSize: 15,
                        letterSpacing: "0.01em",
                        padding: "16px 24px",
                        border: 0,
                        borderRadius: 0,
                        cursor: canSubmit ? "pointer" : "not-allowed",
                        opacity: canSubmit ? 1 : 0.35,
                        minHeight: 52,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        transition: "background-color 200ms ease, color 200ms ease",
                    }}
                    onMouseEnter={(e) => {
                        if (!canSubmit) return;
                        e.currentTarget.style.background = isDark
                            ? "var(--bt-brass-soft)"
                            : "var(--bt-navy)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = isDark
                            ? "var(--bt-paper)"
                            : "var(--bt-ink)";
                    }}
                >
                    <span>{loading ? "Sending" : "Send Me The Brief"}</span>
                    <span aria-hidden="true">{loading ? "···" : "→"}</span>
                </button>

                <p
                    className="f-helper"
                    style={{ color: helperColor, margin: 0 }}
                >
                    Free. One email a month. Unsubscribe in one click.
                </p>
                <p
                    className="f-helper"
                    style={{
                        color: helperColor,
                        margin: 0,
                        marginTop: 8,
                        fontSize: 12,
                        lineHeight: 1.5,
                    }}
                    data-testid={`${instance}-legal-consent-line`}
                >
                    By submitting, you agree to our{" "}
                    <a
                        href="/terms"
                        style={{ color: "inherit", textDecoration: "underline" }}
                    >
                        Terms of Service
                    </a>{" "}
                    and{" "}
                    <a
                        href="/privacy"
                        style={{ color: "inherit", textDecoration: "underline" }}
                    >
                        Privacy Policy
                    </a>
                    .
                </p>
            </div>

            {errorMsg && (
                <p
                    role="alert"
                    style={{
                        color: isDark ? "#E8B4A8" : "var(--bt-error)",
                        fontFamily: "Geist, system-ui, sans-serif",
                        fontSize: 13,
                        margin: 0,
                    }}
                    data-testid={`${instance}-error-line`}
                >
                    {errorMsg}
                </p>
            )}
        </form>
    );
}

function Field({ id, label, labelColor, children }) {
    return (
        <div>
            <label
                className="f-label"
                htmlFor={id}
                style={{ color: labelColor }}
            >
                {label}
            </label>
            {children}
        </div>
    );
}

function ErrorLine({ children }) {
    return (
        <p
            role="alert"
            style={{
                marginTop: 8,
                fontFamily: "Geist, system-ui, sans-serif",
                fontSize: 12,
                color: "var(--bt-error)",
                lineHeight: 1.4,
            }}
        >
            {children}
        </p>
    );
}

function TcpaConsent({ text, dark }) {
    return (
        <p
            data-testid="tcpa-consent-paragraph"
            style={{
                fontFamily: "Geist, system-ui, sans-serif",
                fontSize: 11,
                lineHeight: 1.6,
                color: dark ? "rgba(244,239,229,0.65)" : "var(--bt-mute)",
                margin: 0,
                paddingTop: 4,
                animation: "bt-fade 280ms ease-out",
            }}
        >
            {text}
        </p>
    );
}
